import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CaretRight, CaretLeft, MapPin, PhoneCall, Compass, Buildings, UsersThree } from "@phosphor-icons/react";

// The three strategic-axis dimensions ScaledEasy tags on every session
// (populated from the public feed — see /app/backend/services/approach_sessions.py).
const DIMENSIONS = [
  { key: "Improve Company Aim",         icon: Compass,     axis: "Strategy",  note: "Where the company is going — vision, market, offer clarity." },
  { key: "Reduce Dependence on Owner",  icon: Buildings,   axis: "Structure", note: "How the company runs without you — roles, systems, cadences." },
  { key: "Improve Team Capability",     icon: UsersThree,  axis: "Talent",    note: "Who does the work — hiring, training, growing the bench." },
];

// Inline landing section — 10-at-a-time paginated list of the 100 sessions,
// sourced live from ScaledEasy's public feed (see /app/backend/services/approach_sessions.py).
// Visually echoes the ScaledEasy portal but rendered in CVC's brand.
export default function TheApproach() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ sessions: [], total: 0, pages: 10 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/approach/sessions?page=${page}&size=10`)
      .then((r) => setData(r.data))
      .catch(() => setData({ sessions: [], total: 0, pages: 10 }))
      .finally(() => setLoading(false));
  }, [page]);

  const rangeStart = (page - 1) * 10 + 1;
  const rangeEnd = Math.min(page * 10, data.total || 100);
  const totalPages = data.pages || 10;

  return (
    <section id="approach" className="py-16 md:py-24 bg-cvc-parchment/40">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        {/* Framing — same-program-two-delivery-modes */}
        <div className="grid lg:grid-cols-12 gap-10 items-start mb-12">
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.3em] uppercase font-body font-semibold text-cvc-red">The Approach · 100-step scale-up</p>
            <h2 data-testid="approach-section-h2" className="font-heading text-4xl md:text-5xl text-cvc-navy mt-4 leading-tight">
              The same 100-step scale-up — delivered on-site in Cache Valley.
            </h2>
            <p className="font-body text-cvc-ink-soft text-lg mt-6 leading-relaxed max-w-2xl">
              This is the exact program our nationwide sister brand{" "}
              <a href="https://www.scaledeasy.com/" target="_blank" rel="noopener noreferrer" className="text-cvc-navy underline underline-offset-4 decoration-cvc-border hover:decoration-cvc-navy">ScaledEasy.com</a>{" "}
              delivers by phone through trained specialists. Here in Logan, Jonathan walks you through it{" "}
              <span className="italic">in person</span>, one on-site session at a time — floor walks,
              working sessions at your table, real accountability. Same arc, delivered locally.
            </p>
          </div>
          <div className="lg:col-span-5 rounded-2xl border border-cvc-border bg-white p-6 shadow-sm">
            <p className="text-[10px] tracking-[0.3em] uppercase font-body font-semibold text-cvc-red">Two delivery modes · one curriculum</p>
            <div className="mt-4 space-y-3 font-body text-sm text-cvc-ink">
              <div className="flex items-start gap-3">
                <MapPin size={18} weight="duotone" className="text-cvc-navy shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-cvc-navy">Cache Valley Consulting</p>
                  <p className="text-cvc-ink-soft text-[13px]">Local · in person · Jonathan Johnson</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <PhoneCall size={18} weight="duotone" className="text-scaled-green shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-cvc-navy">ScaledEasy.com</p>
                  <p className="text-cvc-ink-soft text-[13px]">National · by phone · trained specialists</p>
                </div>
              </div>
            </div>
            <p className="mt-4 pt-4 border-t border-cvc-border font-body text-[12px] text-cvc-ink-soft italic leading-relaxed">
              Session titles are curated centrally on ScaledEasy and auto-sync here daily —
              so what you see is always current.
            </p>
          </div>
        </div>

        {/* Three strategic-axis dimensions (Strategy · Structure · Talent) */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {DIMENSIONS.map((d) => {
            const Icon = d.icon;
            return (
              <div
                key={d.key}
                data-testid={`approach-dimension-${d.axis.toLowerCase()}`}
                className="rounded-2xl border border-cvc-border bg-white p-5"
              >
                <div className="flex items-center gap-2">
                  <Icon size={20} weight="duotone" className="text-cvc-red" />
                  <p className="text-[10px] tracking-[0.3em] uppercase font-body font-semibold text-cvc-red">{d.axis}</p>
                </div>
                <h3 className="font-heading text-lg text-cvc-navy mt-2 leading-tight">{d.key}</h3>
                <p className="font-body text-[12px] text-cvc-ink-soft mt-2 leading-snug">{d.note}</p>
              </div>
            );
          })}
        </div>

        {/* Session list — 10 at a time */}
        <div className="rounded-3xl bg-white border border-cvc-border overflow-hidden shadow-sm">
          <div className="flex items-baseline justify-between px-6 py-5 border-b border-cvc-border">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase font-body font-semibold text-cvc-ink-soft/70">Upcoming sessions</p>
              <h3 className="font-heading text-2xl md:text-3xl text-cvc-navy mt-1 leading-tight">Prepare for what's ahead.</h3>
            </div>
            <p className="font-body text-[10px] tracking-[0.3em] uppercase font-semibold text-cvc-red">
              Sessions {rangeStart}–{rangeEnd}
            </p>
          </div>

          <ol data-testid="approach-inline-list" className="divide-y divide-cvc-border">
            {loading ? (
              <li className="p-6 font-body text-sm text-cvc-ink-soft">Loading sessions…</li>
            ) : data.sessions.length === 0 ? (
              <li className="p-6 font-body text-sm text-cvc-ink-soft">No sessions on this page.</li>
            ) : (
              data.sessions.map((s) => (
                <li
                  key={s.number}
                  data-testid={`approach-inline-session-${s.number}`}
                  className="flex items-center gap-5 px-6 py-4 hover:bg-cvc-parchment/40 transition-colors"
                >
                  <span className="shrink-0 w-10 h-10 rounded-full bg-cvc-parchment/70 border border-cvc-border flex items-center justify-center font-body text-sm text-cvc-ink-soft">
                    {s.number}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`font-heading text-lg text-cvc-navy leading-snug ${s.is_placeholder ? "italic text-cvc-ink-soft" : ""}`}>
                      {s.title}
                      <span className="font-body text-[13px] text-cvc-ink-soft/75 italic ml-2">
                        — {s.dimension || s.category}
                      </span>
                    </p>
                  </div>
                  <CaretRight size={18} weight="bold" className="text-cvc-border shrink-0" />
                </li>
              ))
            )}
          </ol>

          {/* Bottom pagination — matches ScaledEasy portal pattern (< Prev 10 · View 21-30 >) */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-cvc-border bg-cvc-parchment/30">
            <Button
              data-testid="approach-inline-prev"
              variant="ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="font-body text-sm text-cvc-ink-soft hover:text-cvc-navy disabled:opacity-30"
            >
              <CaretLeft size={16} weight="bold" className="mr-1" /> Previous 10
            </Button>
            <div className="flex items-center gap-1.5 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  data-testid={`approach-inline-page-${p}`}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-full font-body text-xs transition-colors ${p === page ? "bg-cvc-navy text-cvc-cream" : "bg-white border border-cvc-border text-cvc-ink hover:border-cvc-navy"}`}
                >
                  {p}
                </button>
              ))}
            </div>
            <Button
              data-testid="approach-inline-next"
              variant="ghost"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="font-body text-sm text-cvc-ink-soft hover:text-cvc-navy disabled:opacity-30"
            >
              View Sessions {page * 10 + 1}–{Math.min((page + 1) * 10, data.total || 100)} <CaretRight size={16} weight="bold" className="ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
